import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UserDocument } from "../../models/UserDocument";
import { documentsAPI } from "../../apis";
import { EditUserDocument } from "./EditUserDocument";
import { VersionsUserDocument } from "./VersionsUserDocument";
import trashCan1 from "../../assets/trash-can-1x.png";
import trashCan2 from "../../assets/trash-can-2x.png";
import trashCan3 from "../../assets/trash-can-3x.png";
import { CreateUserDocument } from "./CreateOrEditUserDocument";
import { ViewUserDocument } from "./ViewUserDocument";

export enum Modes {
  create = "create",
  view = "view",
  edit = "edit",
  delete = "delete",
  versions = "versions",
}

const dialogContentStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 0,
};

interface Props {
  open: boolean;
  onComplete?: () => void;
  onUpdateDoc?: () => void;
  onCancel?: () => void;
  document: UserDocument;
  documentSrc: string;
  includeOnlyDocumentTypeIds?: number[]
}

export interface UserDocumentModesProps {
  document: UserDocument;
  documentSrc: string;
  currentMode: Modes;
  setMode: (mode: Modes) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onUpdateDoc?: () => void;
}

export const UserDocumentDialog = ({
  open,
  onComplete,
  onUpdateDoc,
  onCancel,
  document,
  documentSrc,
  includeOnlyDocumentTypeIds,
}: Props): JSX.Element => {
  const [currentMode, setCurrentMode] = useState<Modes | undefined>();
  // const [tempDocument, setTempDocument] = useState<UserDocument>();

  useEffect(() => {
    setCurrentMode(document?.id ? Modes.edit : Modes.create);
  }, [document]);

  const getModeContentTitle = (): JSX.Element | string => {
    if (!document || !currentMode) return <></>;

    switch (currentMode) {
      case Modes.view: {
        return document.version?.title || "Document";
      }
      case Modes.create:
        return "Upload a document";
      case Modes.edit:
        return "Edit document";
      case Modes.delete:
        return "Delete document";
      case Modes.versions:
        if (!document?.version?.title) {
          return "Versions of file";
        }
        return `Versions of file "${document?.version?.title}"`;
    }
  };

  const getModeContent = (): JSX.Element => {
    if (!document || !currentMode) return <></>;

    switch (currentMode) {
      case Modes.view: {
        return (
          <>
            <ViewUserDocument
              document={document}
              documentSrc={documentSrc}
              setMode={setCurrentMode}
            />
          </>
        );
      }
      case Modes.create:
        return (
          <>
            <CreateUserDocument
              onCancel={() => onCancel?.()}
              onComplete={() => onComplete?.()}
              includeOnlyDocumentTypeIds={includeOnlyDocumentTypeIds}
            />
          </>
        );
      case Modes.edit:
        return (
          <>
            <EditUserDocument
              document={document}
              documentSrc={documentSrc}
              onCancel={() => onCancel?.()}
              onComplete={() => onComplete?.()}
              onUpdateDoc={() => onUpdateDoc?.()}
              onClickThumbnail={() => setCurrentMode(Modes.view)}
              includeOnlyDocumentTypeIds={includeOnlyDocumentTypeIds}
              setMode={setCurrentMode}
            />
          </>
        );
      case Modes.delete:
        return (
          <DeleteUserDocument
            document={document}
            documentSrc={documentSrc}
            currentMode={currentMode}
            onComplete={onComplete}
            setMode={setCurrentMode}
          />
        );
      case Modes.versions:
        return (
          <>
            <VersionsUserDocument
              document={document}
              setMode={setCurrentMode}
              onComplete={() => onComplete?.()}
            />
          </>
        );
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={() => (onCancel ? onCancel() : null)}
    >
      <DialogTitle id="responsive-dialog-title" style={{ textAlign: "center" }}>
        {<Typography variant="h3">{getModeContentTitle()}</Typography>}
        {onCancel ? (
          <IconButton
            aria-label="close"
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              color: "grey.500",
            }}
            onClick={onCancel}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent sx={dialogContentStyle}>{getModeContent()}</DialogContent>
    </Dialog>
  );
};

const DeleteUserDocument = ({
  document,
  setMode,
  onComplete,
}: UserDocumentModesProps): JSX.Element => {
  const handleDelete = async () => {
    document && (await documentsAPI.deleteDocument(document));
    onComplete?.();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 100,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          textAlign: "center",
          maxWidth: 500,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            my: 2,
          }}
        >
          <img
            srcSet={`${trashCan2} 2x, ${trashCan3} 3x`}
            src={trashCan1}
            alt="Trash car"
            // style={{ width: "100%" }}
          />
        </Box>
        <Typography variant="h4">
          Are you sure you want to delete "{document?.version?.title}"?
        </Typography>
        <Typography>You won't be able to undo this action.</Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          alignItems: "center",
          "& button": {
            my: 1,
          },
          mt: 2,
        }}
      >
        <Button variant="contained" onClick={() => handleDelete()}>
          Delete
        </Button>
        <Button variant="text" onClick={() => setMode(Modes.edit)}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
